import { AbstractBrandService } from '@abstract-services/brand/1.0/brand.abstract-service';
import * as ngCommonHttp from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BrandAPI } from '@constants/api';
import { ApiResponse } from '@interfaces/api-response/1.0';
import {
  Tenant,
  AggregatorTemplate,
  Tenants,
  TenantConfigurations,
  ServiceType,
  Amenity,
  AmenityCollectionResponse,
  UpdateAmenitiesResponse,
  SmsNotifications,
} from '@generated/tcea/models';
import { SmsCredentialsRequest } from '@models/twilio-credentials/1.0/twilio-credentials.model';
import { MenuJob } from '@interfaces/menu-job/1.0';
import { PaginationService } from '@services/pagination/1.0/pagination.service';
import { BehaviorSubject, Observable, of, throwError, combineLatest } from 'rxjs';
import { catchError, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { TenantsService, NotificationSettingsService } from '@generated/tcea/services';
import { GetTenantAmenities$Params } from '@generated/tcea/fn/tenants/get-tenant-amenities';
import { PutTenantAmenities$Params } from '@generated/tcea/fn/tenants/put-tenant-amenities';
import { GetNotificationSetting$Params } from '@generated/tcea/fn/notification-settings/get-notification-setting';
import { UpdateNotificationSetting$Params } from '@generated/tcea/fn/notification-settings/update-notification-setting';
import { NotificationSettingsResponse } from '@generated/tcea/models';
import { AuthService } from '@auth0/auth0-angular';
import { DaypartsService } from '@generated/tcea/services';
import { PermissionsService } from '@services/permissions/1.0/permissions.service';
import { DaypartsPost$Params } from '@generated/tcea/fn/dayparts/dayparts-post';
import { DaypartsDaypartIdPut$Params } from '@generated/tcea/fn/dayparts/dayparts-daypart-id-put';
import { DaypartsGet$Params } from '@generated/tcea/fn/dayparts/dayparts-get';
import { DaypartRequest } from '@generated/tcea/models';

const DEPRECATED_SERVICE_TYPES = {
  [ServiceType.DELIVERY_BY_RESTAURANT]: true,
};

export function removeDeprecatedServiceTypes(serviceTypes: ServiceType[]): ServiceType[] {
  return serviceTypes?.filter((item) => !DEPRECATED_SERVICE_TYPES[item]) || [];
}

export function removeDeprecatedServiceTypesFromBrand(brand: Tenant): Tenant {
  return {
    ...brand,
    supportedServiceTypes: removeDeprecatedServiceTypes(brand?.supportedServiceTypes) || [],
  };
}

function removePp5FromBrandAggregatorIds(brand: Partial<Tenant>): Partial<Tenant> {
  if (!Array.isArray(brand?.aggregatorIds)) {
    return brand;
  }

  return {
    ...brand,
    aggregatorIds: brand.aggregatorIds.filter((id) => id !== 'pp5'),
  };
}

@Injectable({
  providedIn: 'root',
})
export class BrandService extends AbstractBrandService {
  currentBrand: Tenant;
  currentBrandBehaviorSubject: BehaviorSubject<Tenant> = new BehaviorSubject<Tenant>({
    name: 'YOUR BRANDS',
    isSet: false,
  });

  currentBrandListBehaviorSubject: BehaviorSubject<Tenant[]> = new BehaviorSubject<Tenant[]>([]);
  private tenantRequest$: Observable<Tenant[]> | null = null;
  showResellerFilter$: Observable<boolean>;
  resellerId$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  resellerLogoUrl$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private userId: string | null = null;

  apiUrl: string;
  isLoadingCurrent = false;

  constructor(
    private http: ngCommonHttp.HttpClient,
    private paginationService: PaginationService,
    private translateService: TranslateService,
    private authService: AuthService,
    private generatedTenantService: TenantsService,
    private notificationSettingsService: NotificationSettingsService,
    private daypartsService: DaypartsService,
    private permissionsService: PermissionsService,
  ) {
    super();
    this.apiUrl = BrandAPI.GET;
    const currentBrandId = sessionStorage.getItem('brand_id');
    if (currentBrandId) this.getAndSetCurrentBrand(currentBrandId);

    // Initialize resellerId$ from sessionStorage
    const resellerId = sessionStorage.getItem('reseller_id');
    this.resellerId$.next(resellerId);

    // Initialize resellerLogoUrl$ from sessionStorage if resellerId exists
    if (resellerId) {
      const cachedLogoUrl = sessionStorage.getItem(`reseller_logo_url_${resellerId}`);
      if (cachedLogoUrl) {
        this.resellerLogoUrl$.next(cachedLogoUrl);
      }
    }

    // Create observable that checks permission after permissions are loaded
    this.showResellerFilter$ = combineLatest([this.permissionsService.loaded$]).pipe(
      map(() => this.permissionsService.getPermission('reseller.tenant:read')),
    );

    // Track user changes and clear cache
    this.authService.user$.subscribe((user) => {
      const newUserId = user?.sub;
      if (newUserId && newUserId !== this.userId) {
        this.userId = newUserId;
        this.clearCache();
      }
    });
  }

  // HTTP Functions
  create(brand: Partial<Tenant>, createSandBox?: boolean): Observable<Tenant> {
    if (createSandBox) {
      return this.authService.user$.pipe(
        take(1),
        switchMap((user) => {
          const userEmail = user?.email || '';
          const body = {
            process: 'TENANT',
            user: userEmail,
            tenant: {
              name: brand.name,
              alias: brand.alias,
              countryCode: 'US',
              email: brand.email,
            },
          };
          return this.http.post<Tenant>(`${this.apiUrl}sandbox/?action=create`, body);
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => {
          console.error('HTTP Error:', err);
          return throwError(() => err);
        }),
      );
    } else {
      const BODY = brand;

      return this.http.post(this.apiUrl, BODY).pipe(
        map((response: Tenant) => {
          return response;
        }),
        catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(() => err)),
      );
    }
  }

  get(): Observable<Tenant[]> {
    let list: Tenant[] = this.currentBrandListBehaviorSubject.getValue();

    if (list?.length) {
      return of(list);
    }

    if (!this.tenantRequest$) {
      const headers = new ngCommonHttp.HttpHeaders({
        'Cache-Control': 'no-cache, no-store, must-revalidate',
      });

      this.tenantRequest$ = this.paginationService
        .fetchAllPages<Tenants>(
          `${this.apiUrl}?size=500&field=name&field=countryCode&field=alias&field=resellerId`,
          { headers },
        )
        .pipe(
          map((response: Tenants) => {
            if (response && response._embedded?.tenant) {
              const RESULT: Tenant[] = response._embedded.tenant;
              list = [
                ...list,
                ...RESULT.map((item) => removeDeprecatedServiceTypesFromBrand(item)),
              ];
              this.currentBrandListBehaviorSubject.next(list);
              return RESULT;
            }
            return [];
          }),
          catchError((err: ngCommonHttp.HttpErrorResponse) => {
            this.tenantRequest$ = null; // Reset cache on error
            return throwError(err);
          }),
          shareReplay(1),
        );
    }

    return this.tenantRequest$;
  }

  /**
   * Clears the internal brands list cache
   */
  clearCache(): void {
    this.currentBrandListBehaviorSubject.next([]);
    this.tenantRequest$ = null;
  }

  getConfigs(): Observable<TenantConfigurations> {
    return this.http.get(`/tce-experience-admin/tenant-configurations`).pipe(
      map((response: TenantConfigurations) => {
        let settings: any = response.settings;
        const supportPizzaItem = Array.isArray(response.uiSettings)
          ? response.uiSettings.find((item) => item?.id === 'supportPizzaItem')
          : undefined;

        if (supportPizzaItem) {
          settings.push(supportPizzaItem);
        }
        Object.keys(settings).forEach((setting) => {
          const label = this.translateService.instant(
            'brand.settings.generalSettings.' + response?.settings[setting]['id'],
          );
          if (label.includes('brand.settings')) delete response.settings[setting];
        });
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  save(request: Partial<Tenant>, brandId: string): Observable<any> {
    const BODY = request;
    return this.http.put(`${this.apiUrl}${brandId}`, BODY).pipe(
      map((response: ApiResponse) => {
        this.getOne(brandId).subscribe((brandPartial) => this.setCurrentContext(brandPartial));
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  getOne(brandId: string): Observable<Tenant> {
    return this.http.get(`${this.apiUrl}${brandId}/`).pipe(
      map((response: Tenant) => {
        return removeDeprecatedServiceTypesFromBrand(response);
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  updateAggregators(brandId: string, aggregatorConfigs: AggregatorTemplate): Observable<Tenant> {
    const BODY = {
      aggregatorConfigurations: aggregatorConfigs,
    };

    return this.http.put(`${this.apiUrl}${brandId}/aggregators`, BODY).pipe(
      map((response: Tenant) => {
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  getMenuJobs(brandId: string, params?: string): Observable<MenuJob[]> {
    let url = `${this.apiUrl}${brandId}/menu-extract`;
    if (params) {
      url = `${url}${params}`;
    }
    return this.paginationService.fetchPage(url).pipe(
      map((response: any) => {
        if (response._embedded) {
          return response._embedded.menuJobs;
        } else if (response.results) {
          return response.results;
        } else {
          return undefined;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  // helpers
  clean(brandObj): Tenant {
    const brand = brandObj;
    if ('id' in brand) {
      delete brand.id;
    }
    return brand;
  }

  // brand context setting
  getAndSetCurrentBrand(brandId: string) {
    if (this.isLoadingCurrent) {
      return;
    }
    this.isLoadingCurrent = true;
    this.getOne(brandId).subscribe((brand) => {
      this.setCurrentContext(brand);
      this.isLoadingCurrent = false;
    });
  }

  /**
   * Sets current brand context
   *
   * @param brand (Partial) - Brand Name and ID for context checks
   * @returns void
   */
  setCurrentContext(brand: Partial<Tenant>): void {
    this.currentBrand = removePp5FromBrandAggregatorIds(brand) as Tenant;
    this.currentBrand.isSet = true;
    sessionStorage.setItem('brand_id', this.currentBrand.id);
    this.currentBrandBehaviorSubject.next(this.currentBrand);

    // If brand has a resellerId, check for cached logo in sessionStorage
    if (this.currentBrand.resellerId) {
      const cachedLogoUrl = sessionStorage.getItem(
        `reseller_logo_url_${this.currentBrand.resellerId}`,
      );
      if (cachedLogoUrl) {
        this.resellerLogoUrl$.next(cachedLogoUrl);
      } else {
        // Clear logo if no cached logo found for this reseller
        this.resellerLogoUrl$.next(null);
      }
    } else {
      // Clear logo if brand doesn't have a resellerId
      this.resellerLogoUrl$.next(null);
    }
  }

  clearCurrentContext(): void {
    this.currentBrand = null;
    sessionStorage.removeItem('brand_id'); // Remove stored brand ID
    this.currentBrandBehaviorSubject.next(null); // Reset observable
  }

  reloadWithNewBrand(brandId: string, url: string) {
    sessionStorage.setItem('brand_id', brandId);
    window.location.href = url;
  }

  getAmenities(brandId: string): Observable<Amenity[]> {
    let params: GetTenantAmenities$Params = {
      size: 100,
      'Active-Tenant-Identifier': brandId,
    };

    return this.generatedTenantService.getTenantAmenities(params).pipe(
      map((response: AmenityCollectionResponse) => {
        if (response) {
          return response.results;
        } else {
          return undefined;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  saveAmenities(brandId: string, amenities: Amenity[]): Observable<any[]> {
    const amenitiesUpdateRequest = {
      amenities: amenities.map((a) => {
        return {
          id: a.id,
          active: a.active,
        };
      }),
    };
    let params: PutTenantAmenities$Params = {
      'Active-Tenant-Identifier': brandId,
      body: amenitiesUpdateRequest,
    };
    return this.generatedTenantService.putTenantAmenities(params).pipe(
      map((response: UpdateAmenitiesResponse) => {
        if (response) {
          return response.amenities;
        } else {
          return undefined;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  getSmsNotificationSettings(brandId: string): Observable<any> {
    const params: GetNotificationSetting$Params = {
      tenantId: brandId,
      notificationType: 'SMS',
      'Active-Tenant-Identifier': brandId,
    };

    return this.notificationSettingsService.getNotificationSetting(params).pipe(
      map((response: NotificationSettingsResponse) => {
        // Return enabled flag and all settings (including credentials)
        return { enabled: response.enabled, ...response.settings };
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  saveSmsNotificationSettings(brandId: string, smsNotifications: any): Observable<void> {
    const params: UpdateNotificationSetting$Params = {
      tenantId: brandId,
      notificationType: 'SMS',
      'Active-Tenant-Identifier': brandId,
      body: smsNotifications,
    };

    return this.notificationSettingsService
      .updateNotificationSetting(params)
      .pipe(catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)));
  }

  saveSmsCredentials(brandId: string, credentials: SmsCredentialsRequest): Observable<void> {
    const url = `${BrandAPI.GET}${brandId}/notification-settings/sms/credentials`;

    return this.http
      .put<void>(url, credentials, {})
      .pipe(catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)));
  }

  // Dayparts methods
  createDaypart(brandId: string, daypartData: DaypartRequest): Observable<{ daypartId?: string }> {
    const params: DaypartsPost$Params = {
      'Active-Tenant-Identifier': brandId,
      body: daypartData,
    };

    return this.daypartsService.daypartsPost(params).pipe(
      map((response) => {
        return response;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => {
        console.error('Error creating daypart:', err);
        return throwError(err);
      }),
    );
  }

  updateDaypart(brandId: string, daypartId: string, daypartData: DaypartRequest): Observable<void> {
    const params: DaypartsDaypartIdPut$Params = {
      'Active-Tenant-Identifier': brandId,
      daypartId: daypartId,
      body: daypartData,
    };

    return this.daypartsService
      .daypartsDaypartIdPut(params)
      .pipe(catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)));
  }

  getDayparts(brandId: string): Observable<any> {
    const params: DaypartsGet$Params = {
      'Active-Tenant-Identifier': brandId,
    };

    return this.daypartsService.daypartsGet(params).pipe(
      map((response) => {
        const dayparts = response?._embedded?.dayparts || [];
        const dayPartMap = {};
        dayparts.forEach((daypart) => {
          if (daypart.daypartId) {
            dayPartMap[daypart.daypartId] = {
              id: daypart.daypartId,
              name: daypart.name,
            };
          }
        });
        return dayPartMap;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => {
        return of({});
      }),
    );
  }

  /**
   * Sets the reseller ID and updates both the observable and sessionStorage
   * @param resellerId The reseller ID to set, or null to clear it
   */
  setResellerId(resellerId: string | null): void {
    const oldResellerId = this.resellerId$.getValue();

    // Clear old logo from sessionStorage if reseller ID is changing
    if (oldResellerId && oldResellerId !== resellerId) {
      sessionStorage.removeItem(`reseller_logo_url_${oldResellerId}`);
      this.resellerLogoUrl$.next(null);
    }

    if (resellerId) {
      sessionStorage.setItem('reseller_id', resellerId);
      // Restore logo from sessionStorage if available
      const cachedLogoUrl = sessionStorage.getItem(`reseller_logo_url_${resellerId}`);
      if (cachedLogoUrl) {
        this.resellerLogoUrl$.next(cachedLogoUrl);
      }
    } else {
      sessionStorage.removeItem('reseller_id');
    }
    this.resellerId$.next(resellerId);
  }

  /**
   * Sets the reseller logo URL in cache
   * @param logoUrl The logo URL to cache, or null to clear it
   * @param resellerId Optional reseller ID. If not provided, uses the current resellerId from BrandService
   */
  setResellerLogoUrl(logoUrl: string | null, resellerId?: string | null): void {
    const targetResellerId = resellerId ?? this.resellerId$.getValue();
    if (logoUrl && targetResellerId) {
      // Store in sessionStorage with resellerId as key
      sessionStorage.setItem(`reseller_logo_url_${targetResellerId}`, logoUrl);
    } else if (targetResellerId) {
      // Clear from sessionStorage if logoUrl is null
      sessionStorage.removeItem(`reseller_logo_url_${targetResellerId}`);
    }
    this.resellerLogoUrl$.next(logoUrl);
  }

  /**
   * Gets the cached reseller logo URL
   * @returns The cached logo URL or null
   */
  getResellerLogoUrl(): string | null {
    return this.resellerLogoUrl$.getValue();
  }
}
